import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericModal from "@ui-components/Modal/GenericModal";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import ReflectorSelector from "../shared/ReflectorSelector";
import ReflectorMultiSelector from "../shared/ReflectorMultiSelector";
import {
  LogCollectorConfig,
  CreateConfigPayload,
  createConfig,
  deleteConfig,
  fetchCurrentConfig,
  getConfigs,
  publishConfig,
  updateConfig,
} from "./request";

const SCOPE_OPTIONS: { value: LogCollectorConfig["scopeType"]; label: string }[] = [
  { value: "global", label: "Global (all reflectors)" },
  { value: "tenant", label: "Tenant (all reflectors of a tenant)" },
  { value: "reflector", label: "Single Reflector" },
];

interface EditorState {
  label: string;
  scopeType: LogCollectorConfig["scopeType"];
  scopeId: string;
  fluentBitConf: string;
  ctxrLua: string;
}

const DEFAULT_EDITOR: EditorState = {
  label: "",
  scopeType: "global",
  scopeId: "",
  fluentBitConf: "",
  ctxrLua: "",
};

const LogCollectorConfigPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [configs, setConfigs] = useState<LogCollectorConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<LogCollectorConfig | null>(null);
  const [editor, setEditor] = useState<EditorState>(DEFAULT_EDITOR);

  const [publishTargetUuids, setPublishTargetUuids] = useState<string[]>([]);
  const [publishing, setPublishing] = useState<number | null>(null);
  const [publishResult, setPublishResult] = useState<{
    id: number;
    published: string[];
    failed: string[];
  } | null>(null);

  const [fetchUuidInput, setFetchUuidInput] = useState<string>("");
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState<{
    fluentBitConf: string | null;
    ctxrLua: string | null;
    ctxrUuid: string;
  } | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const res = await getConfigs(token);
      if (res.data.success) {
        setConfigs(res.data.data);
      }
    } catch {
      setErrorMessage("Failed to load configs.");
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateModal = () => {
    setEditingConfig(null);
    setEditor(DEFAULT_EDITOR);
    setIsModalOpen(true);
  };

  const openEditModal = (cfg: LogCollectorConfig) => {
    setEditingConfig(cfg);
    setEditor({
      label: cfg.label ?? "",
      scopeType: cfg.scopeType,
      scopeId: cfg.scopeId ?? "",
      fluentBitConf: cfg.fluentBitConf ?? "",
      ctxrLua: cfg.ctxrLua ?? "",
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = await getAccessTokenSilently();
      const payload: CreateConfigPayload = {
        scopeType: editor.scopeType,
        scopeId: editor.scopeType === "global" ? undefined : editor.scopeId,
        fluentBitConf: editor.fluentBitConf || undefined,
        ctxrLua: editor.ctxrLua || undefined,
        label: editor.label || undefined,
      };
      if (editingConfig) {
        await updateConfig(editingConfig.id, payload, token);
      } else {
        await createConfig(payload, token);
      }
      setIsModalOpen(false);
      load();
    } catch {
      setErrorMessage("Failed to save config.");
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = globalThis.confirm("Delete this config?");
    if (!confirmed) return;
    try {
      const token = await getAccessTokenSilently();
      await deleteConfig(id, token);
      load();
    } catch {
      setErrorMessage("Failed to delete config.");
    }
  };

  const handlePublish = async (cfg: LogCollectorConfig) => {
    setPublishing(cfg.id);
    setPublishResult(null);
    try {
      const token = await getAccessTokenSilently();
      const targets = publishTargetUuids.length > 0
        ? { ctxrUuids: publishTargetUuids }
        : {};
      const res = await publishConfig(cfg.id, targets, token);
      if (res.data.success) {
        setPublishResult({ id: cfg.id, ...res.data.data });
      }
    } catch {
      setErrorMessage("Failed to publish config.");
    } finally {
      setPublishing(null);
    }
  };

  const handleFetchCurrent = async () => {
    const uuid = fetchUuidInput.trim();
    if (!uuid) return;
    setFetching(true);
    setFetchResult(null);
    setFetchError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetchCurrentConfig(uuid, token);
      if (res.data.success) {
        setFetchResult({
          ctxrUuid: res.data.data.ctxrUuid,
          fluentBitConf: res.data.data.fluentBitConf,
          ctxrLua: res.data.data.ctxrLua,
        });
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setFetchError(msg ?? "Reflector did not respond in time.");
    } finally {
      setFetching(false);
    }
  };

  const scopeLabel = (cfg: LogCollectorConfig) => {
    if (cfg.scopeType === "global") return "Global";
    if (cfg.scopeType === "tenant") return `Tenant: ${cfg.scopeId}`;
    return `Reflector: ${cfg.scopeId}`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Log Collector Config</h1>
        <GenericButton label="New Config" variant="brand" onClick={openCreateModal} />
      </div>

      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
          {errorMessage}
          <button className="ml-4 underline" onClick={() => setErrorMessage(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Fetch Current Config section */}
      <GenericCard isCollapsible={false} title="Fetch Current Config from Reflector">
        <div className="flex items-center gap-3 flex-wrap">
          <div style={{ flex: 1, minWidth: 280 }}>
            <ReflectorSelector
              id="fetch-reflector-uuid"
              value={fetchUuidInput}
              onChange={setFetchUuidInput}
              disabled={fetching}
              placeholder="Select a reflector to fetch its config…"
            />
          </div>
          <GenericButton
            label={fetching ? "Fetching…" : "Fetch"}
            variant="secondary"
            onClick={handleFetchCurrent}
            disabled={fetching || !fetchUuidInput.trim()}
          />
        </div>
        {fetchError && (
          <p className="mt-2 text-sm text-red-600">{fetchError}</p>
        )}
        {fetchResult && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-gray-500">
              Config from <span className="font-mono">{fetchResult.ctxrUuid}</span>
            </p>
            {fetchResult.fluentBitConf && (
              <div>
                <p className="text-xs font-semibold mb-1">fluent-bit.conf</p>
                <textarea
                  readOnly
                  value={fetchResult.fluentBitConf}
                  rows={10}
                  className="w-full font-mono text-xs border rounded p-2 bg-gray-50"
                />
              </div>
            )}
            {fetchResult.ctxrLua && (
              <div>
                <p className="text-xs font-semibold mb-1">ctxr.lua</p>
                <textarea
                  readOnly
                  value={fetchResult.ctxrLua}
                  rows={10}
                  className="w-full font-mono text-xs border rounded p-2 bg-gray-50"
                />
              </div>
            )}
          </div>
        )}
      </GenericCard>

      {/* Config list */}
      <GenericCard isCollapsible={false} title="Saved Configs">
        {loading && <SkeletonTableRows rows={3} cols={4} />}
        {!loading && configs.length === 0 && (
          <p className="text-sm text-gray-500 py-4">No configs saved yet.</p>
        )}
        {!loading && configs.length > 0 && (
          <div className="space-y-4">
            {configs.map((cfg) => (
              <div
                key={cfg.id}
                className="border rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-sm">
                      {cfg.label ?? `Config #${cfg.id}`}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{scopeLabel(cfg)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Last updated {new Date(cfg.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <GenericButton
                      label="Edit"
                      variant="secondary"
                      size="sm"
                      onClick={() => openEditModal(cfg)}
                    />
                    <GenericButton
                      label="Delete"
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(cfg.id)}
                    />
                  </div>
                </div>

                {/* Publish section */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-600">
                    Publish to reflectors
                  </p>
                  <ReflectorMultiSelector
                    value={publishTargetUuids}
                    onChange={setPublishTargetUuids}
                    placeholder="Add reflectors (leave empty to use config scope)…"
                  />
                  <div className="flex justify-end">
                    <GenericButton
                      label={publishing === cfg.id ? "Publishing…" : "Publish"}
                      variant="brand"
                      size="sm"
                      disabled={publishing === cfg.id}
                      onClick={() => handlePublish(cfg)}
                    />
                  </div>
                  {publishResult?.id === cfg.id && (
                    <div className="text-xs space-y-1">
                      {publishResult.published.length > 0 && (
                        <p className="text-green-700">
                          Published to: {publishResult.published.join(", ")}
                        </p>
                      )}
                      {publishResult.failed.length > 0 && (
                        <p className="text-red-600">
                          Failed: {publishResult.failed.join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </GenericCard>

      {/* Create / Edit modal */}
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingConfig ? "Edit Config" : "New Config"}
        size="lg"
      >
        <div className="space-y-4 p-2">
          <div>
            <label htmlFor="cfg-label" className="block text-xs font-semibold mb-1">Label</label>
            <input
              id="cfg-label"
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Optional label"
              value={editor.label}
              onChange={(e) => setEditor({ ...editor, label: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="cfg-scope" className="block text-xs font-semibold mb-1">Scope</label>
            <select
              id="cfg-scope"
              className="w-full border rounded px-3 py-2 text-sm"
              value={editor.scopeType}
              onChange={(e) =>
                setEditor({ ...editor, scopeType: e.target.value as LogCollectorConfig["scopeType"] })
              }
            >
              {SCOPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {editor.scopeType !== "global" && (
            <div>
              <label htmlFor="cfg-scope-id" className="block text-xs font-semibold mb-1">
                {editor.scopeType === "tenant" ? "Tenant ID" : "Reflector"}
              </label>
              {editor.scopeType === "reflector" ? (
                <ReflectorSelector
                  id="cfg-scope-id"
                  value={editor.scopeId}
                  onChange={(subId) => setEditor({ ...editor, scopeId: subId })}
                  placeholder="Select a reflector…"
                />
              ) : (
                <input
                  id="cfg-scope-id"
                  className="w-full border rounded px-3 py-2 text-sm font-mono"
                  placeholder="Tenant ID"
                  value={editor.scopeId}
                  onChange={(e) => setEditor({ ...editor, scopeId: e.target.value })}
                />
              )}
            </div>
          )}

          <div>
            <label htmlFor="cfg-fluent-bit-conf" className="block text-xs font-semibold mb-1">fluent-bit.conf</label>
            <textarea
              id="cfg-fluent-bit-conf"
              className="w-full border rounded px-3 py-2 text-xs font-mono"
              rows={12}
              placeholder="Paste fluent-bit.conf content here…"
              value={editor.fluentBitConf}
              onChange={(e) => setEditor({ ...editor, fluentBitConf: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="cfg-ctxr-lua" className="block text-xs font-semibold mb-1">ctxr.lua</label>
            <textarea
              id="cfg-ctxr-lua"
              className="w-full border rounded px-3 py-2 text-xs font-mono"
              rows={12}
              placeholder="Paste ctxr.lua content here…"
              value={editor.ctxrLua}
              onChange={(e) => setEditor({ ...editor, ctxrLua: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <GenericButton
              label="Cancel"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            />
            <GenericButton label="Save" variant="brand" onClick={handleSave} />
          </div>
        </div>
      </GenericModal>
    </div>
  );
};

export default LogCollectorConfigPage;
